// `turbotwin-sdk/icons`: the icon vocabulary as framework-neutral data (path
// data in a 24x24 viewBox), for React, the portal, and the Unreal export scripts.
import { MATERIAL_PATHS } from './paths.generated.js';
import { ICON_VOCABULARY } from './vocabulary.js';

export { ICON_VOCABULARY, MATERIAL_PATHS };

export type IconGroup = keyof typeof ICON_VOCABULARY;
export type IconConcept<G extends IconGroup> = keyof (typeof ICON_VOCABULARY)[G] & string;
export interface IconSpec {
  /** The concept key, e.g. `twin`. */
  name: string;
  /** The Material icon name, or null for a custom glyph. */
  material: string | null;
  /** SVG path data in a 24x24 viewBox, drawn filled. */
  path: string;
}

function resolve(name: string, entry: string | { path: string }): IconSpec {
  if (typeof entry !== 'string') return { name, material: null, path: entry.path };
  const path = MATERIAL_PATHS[entry];
  if (!path) throw new Error(`icon ${name}: no path for Material icon ${entry}, run node scripts/generate-icon-paths.mjs`);
  return { name, material: entry, path };
}

export const Icons = Object.fromEntries(
  Object.entries(ICON_VOCABULARY).map(([group, concepts]) => [
    group,
    Object.fromEntries(Object.entries(concepts).map(([name, entry]) => [name, resolve(name, entry)])),
  ]),
) as { readonly [G in IconGroup]: { readonly [C in IconConcept<G>]: IconSpec } };

/** The icon as standalone SVG markup (24x24 viewBox, filled with `fill`, default `currentColor`). */
export function iconSvg(spec: IconSpec, opts: { size?: number; fill?: string } = {}): string {
  const size = opts.size ?? 24;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24"><path fill="${opts.fill ?? 'currentColor'}" d="${spec.path}"/></svg>`;
}
